// Outbound webhook config (PRD §4.11 / WP-14). Members read tenants/{t}/config/webhook
// for the current settings; owner/admin mutate via callables. The signing secret
// is never read back to the client — it's minted server-side and returned once
// on save/rotate. The doc only exposes whether a secret exists + last delivery.
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { call } from './callable';
import { db } from './firebase';
import { useTenant } from './tenant';

export const WEBHOOK_EVENTS = [
  'message.received',
  'message.sent',
  'message.status',
  'conversation.status',
  'postback',
  'input_flow.completed',
  'location.received',
  'subscriber.created',
  'subscriber.updated',
  'subscriber.optout',
  'broadcast.completed',
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

export const DATA_FIELDS = [
  'subscriberId',
  'name',
  'phone',
  'dob',
  'location',
  'labels',
  'lists',
  'customFields',
  'postbackId',
  'inputFlowData',
  'conversationId',
  'integrationId',
] as const;
export type DataField = (typeof DATA_FIELDS)[number];

export interface WebhookConfig {
  url: string;
  events: WebhookEvent[];
  dataFields: DataField[];
  enabled: boolean;
  hasSecret: boolean;
  lastStatus: number | null;
  lastOk: boolean | null;
  lastDeliveryAt: number | null;
  lastError: string | null;
}

const EMPTY: WebhookConfig = {
  url: '',
  events: [],
  dataFields: [],
  enabled: false,
  hasSecret: false,
  lastStatus: null,
  lastOk: null,
  lastDeliveryAt: null,
  lastError: null,
};

export function useWebhookConfig(): { config: WebhookConfig; loading: boolean } {
  const { tenantId } = useTenant();
  const [config, setConfig] = useState<WebhookConfig>(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setConfig(EMPTY);
      setLoading(false);
      return;
    }
    const ref = doc(db, `tenants/${tenantId}/config/webhook`);
    return onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() as any;
        setConfig(
          d
            ? {
                url: d.url ?? '',
                events: d.events ?? [],
                dataFields: d.dataFields ?? [],
                enabled: Boolean(d.enabled),
                hasSecret: Boolean(d.secretRef),
                lastStatus: d.lastStatus ?? null,
                lastOk: d.lastOk ?? null,
                lastDeliveryAt: d.lastDeliveryAt ?? null,
                lastError: d.lastError ?? null,
              }
            : EMPTY,
        );
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [tenantId]);

  return { config, loading };
}

const saveFn = call<{ url: string; events: WebhookEvent[]; enabled: boolean; dataFields?: DataField[] }, { ok: boolean; secret: string | null }>('webhookConfigSave');
const rotateFn = call<Record<string, never>, { ok: boolean; secret: string }>('webhookRotateSecret');
const testFn = call<Record<string, never>, { ok: boolean; status?: number; error?: string; skipped?: boolean }>('webhookTest');

export async function saveWebhook(url: string, events: WebhookEvent[], enabled: boolean, dataFields?: DataField[]) {
  const res = await saveFn({ url, events, enabled, dataFields });
  return res.data;
}

export async function rotateWebhookSecret() {
  const res = await rotateFn({});
  return res.data;
}

export async function testWebhook() {
  const res = await testFn({});
  return res.data;
}
